import { supabase } from './supabase';

/**
 * Homepage Content Service
 * Handles loading and saving of homepage content sections
 */

// Contact Information
export const getContactInfo = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_contact_info')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000001')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching contact info:', error);
      return null;
    }

    return data || {
      email: "ryan@example.com",
      location: "San Francisco, CA",
      phone: "+1 (555) 123-4567",
      github: "github.com/yourusername",
      linkedin: "linkedin.com/in/yourusername",
      twitter: "@yourusername",
      website: "yourwebsite.com"
    };
  } catch (error) {
    console.error('Error in getContactInfo:', error);
    return null;
  }
};

export const saveContactInfo = async (contactInfo) => {
  try {
    const { data, error } = await supabase
      .from('homepage_contact_info')
      .upsert({
        id: '00000000-0000-0000-0000-000000000001',
        ...contactInfo
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving contact info:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveContactInfo:', error);
    throw error;
  }
};

// About Me
export const getAboutMe = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_about_me')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000002')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching about me:', error);
      return null;
    }

    return data || {
      title: "About Me",
      bio: `Hello! I'm a full-stack developer passionate about building simple, elegant solutions to complex problems. My journey into coding started with a curiosity for how the web works, which quickly evolved into a dedicated effort to master modern frameworks like React and Angular.

This blog serves as my digital sandbox where I document my technical explorations, share insights from my coding projects (many of which you can find on the **Web Apps** page!), and discuss the intersection of technology and minimalist design.

Currently, I'm focusing on generative AI applications and pushing the boundaries of single-file web deployment. When I'm not coding, you can find me hiking or learning new languages. Feel free to connect!`,
      skills: [
        "Frontend: React, TypeScript, Tailwind CSS",
        "Backend: Supabase (PostgreSQL), Node.js, Serverless Architectures"
      ]
    };
  } catch (error) {
    console.error('Error in getAboutMe:', error);
    return null;
  }
};

export const saveAboutMe = async (aboutMe) => {
  try {
    const { data, error } = await supabase
      .from('homepage_about_me')
      .upsert({
        id: '00000000-0000-0000-0000-000000000002',
        ...aboutMe
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving about me:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveAboutMe:', error);
    throw error;
  }
};

// Reading List
export const getReadingList = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_reading_list')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching reading list:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getReadingList:', error);
    return [];
  }
};

export const saveReadingList = async (items) => {
  try {
    // Delete all existing items
    const { error: deleteError } = await supabase
      .from('homepage_reading_list')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) {
      console.error('Error deleting reading list:', deleteError);
      throw deleteError;
    }

    // Insert new items (exclude id field as it's auto-generated)
    if (items.length > 0) {
      const itemsWithOrder = items.map((item, index) => {
        const { id, ...itemWithoutId } = item;
        return {
          ...itemWithoutId,
          display_order: index + 1
        };
      });

      const { data, error } = await supabase
        .from('homepage_reading_list')
        .insert(itemsWithOrder)
        .select();

      if (error) {
        console.error('Error saving reading list:', error);
        throw error;
      }

      return data;
    }

    return [];
  } catch (error) {
    console.error('Error in saveReadingList:', error);
    throw error;
  }
};

// Recent Updates
export const getRecentUpdates = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_recent_updates')
      .select('*')
      .order('display_order', { ascending: false });

    if (error) {
      console.error('Error fetching recent updates:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getRecentUpdates:', error);
    return [];
  }
};

export const saveRecentUpdates = async (updates) => {
  try {
    // Delete all existing items
    const { error: deleteError } = await supabase
      .from('homepage_recent_updates')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) {
      console.error('Error deleting recent updates:', deleteError);
      throw deleteError;
    }

    // Insert new items (exclude id field as it's auto-generated)
    if (updates.length > 0) {
      const updatesWithOrder = updates.map((update, index) => {
        const { id, ...updateWithoutId } = update;
        return {
          ...updateWithoutId,
          display_order: updates.length - index // Reverse order (newest first)
        };
      });

      const { data, error } = await supabase
        .from('homepage_recent_updates')
        .insert(updatesWithOrder)
        .select();

      if (error) {
        console.error('Error saving recent updates:', error);
        throw error;
      }

      return data;
    }

    return [];
  } catch (error) {
    console.error('Error in saveRecentUpdates:', error);
    throw error;
  }
};

// Web Apps
export const getWebApps = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_web_apps')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching web apps:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getWebApps:', error);
    return [];
  }
};

export const saveWebApps = async (apps) => {
  try {
    // Delete all existing items
    const { error: deleteError } = await supabase
      .from('homepage_web_apps')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) {
      console.error('Error deleting web apps:', deleteError);
      throw deleteError;
    }

    // Insert new items (exclude id field as it's auto-generated)
    if (apps.length > 0) {
      const appsWithOrder = apps.map((app, index) => {
        const { id, ...appWithoutId } = app;
        return {
          ...appWithoutId,
          display_order: index + 1
        };
      });

      const { data, error } = await supabase
        .from('homepage_web_apps')
        .insert(appsWithOrder)
        .select();

      if (error) {
        console.error('Error saving web apps:', error);
        throw error;
      }

      return data;
    }

    return [];
  } catch (error) {
    console.error('Error in saveWebApps:', error);
    throw error;
  }
};

// Quick Links
export const getQuickLinks = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_quick_links')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching quick links:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getQuickLinks:', error);
    return [];
  }
};

export const saveQuickLinks = async (links) => {
  try {
    // Delete all existing items
    const { error: deleteError } = await supabase
      .from('homepage_quick_links')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

    if (deleteError) {
      console.error('Error deleting quick links:', deleteError);
      throw deleteError;
    }

    // Insert new items (exclude id field as it's auto-generated)
    if (links.length > 0) {
      const linksWithOrder = links.map((link, index) => {
        const { id, ...linkWithoutId } = link;
        return {
          ...linkWithoutId,
          display_order: index + 1
        };
      });

      const { data, error } = await supabase
        .from('homepage_quick_links')
        .insert(linksWithOrder)
        .select();

      if (error) {
        console.error('Error saving quick links:', error);
        throw error;
      }

      return data;
    }

    return [];
  } catch (error) {
    console.error('Error in saveQuickLinks:', error);
    throw error;
  }
};

// Feedback
export const submitFeedback = async (feedbackData, userId = null) => {
  try {
    const { data, error } = await supabase
      .from('feedback')
      .insert({
        app_name: feedbackData.app,
        rating: feedbackData.rating,
        subject: feedbackData.subject,
        message: feedbackData.message,
        email: feedbackData.email || null,
        user_id: userId || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in submitFeedback:', error);
    throw error;
  }
};

export const getFeedback = async () => {
  try {
    const { data, error } = await supabase
      .from('feedback')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching feedback:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getFeedback:', error);
    return [];
  }
};

// Blog Intro
export const getBlogIntro = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_blog_intro')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000003')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching blog intro:', error);
      return null;
    }

    return data || {
      title: 'Journal',
      content: 'Welcome to my journal! Here I share my thoughts on development, technology, and the projects I\'m working on — including living docs that evolve over time.'
    };
  } catch (error) {
    console.error('Error in getBlogIntro:', error);
    return null;
  }
};

export const saveBlogIntro = async (intro) => {
  try {
    const { data, error } = await supabase
      .from('homepage_blog_intro')
      .upsert({
        id: '00000000-0000-0000-0000-000000000003',
        ...intro
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving blog intro:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveBlogIntro:', error);
    throw error;
  }
};

// Apps Intro
export const getAppsIntro = async () => {
  try {
    const { data, error } = await supabase
      .from('homepage_apps_intro')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000004')
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching apps intro:', error);
      return null;
    }

    return data || {
      title: 'My Web Applications',
      content: 'Explore the web applications I\'ve built. Each project represents a unique solution to a real-world problem.'
    };
  } catch (error) {
    console.error('Error in getAppsIntro:', error);
    return null;
  }
};

export const saveAppsIntro = async (intro) => {
  try {
    const { data, error } = await supabase
      .from('homepage_apps_intro')
      .upsert({
        id: '00000000-0000-0000-0000-000000000004',
        ...intro
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving apps intro:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in saveAppsIntro:', error);
    throw error;
  }
};
